import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, RefreshCw, Edit3, Sparkles } from "lucide-react";

function formatEsvText(text) {
  if (!text) return null;
  // Split on [N] markers so each verse renders on its own line.
  const parts = text.split(/(\[\d+\])/).filter(Boolean);
  const nodes = [];
  for (let i = 0; i < parts.length; i++) {
    const token = parts[i];
    if (/^\[\d+\]$/.test(token)) {
      nodes.push(
        <sup key={`v${i}`} className="text-[10px] font-semibold text-terracotta mr-1 align-super">
          {token.slice(1, -1)}
        </sup>
      );
    } else {
      nodes.push(<span key={`t${i}`}>{token.trim() + " "}</span>);
    }
  }
  return nodes;
}

export default function DevotionalCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [devo, setDevo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ reference: "", esv_text: "", devotional: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/devotionals/today");
      setDevo(data);
    } catch (e) {
      if (e?.response?.status !== 401) {
        // Show a quiet placeholder but don't spam toasts
        console.warn("Devotional load failed", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/devotionals/today/regenerate");
      setDevo(data);
      toast.success("Devotional regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to regenerate");
    } finally {
      setRegenerating(false);
    }
  };

  const openEdit = () => {
    setForm({
      reference: devo?.reference || "",
      esv_text: devo?.esv_text || "",
      devotional: devo?.devotional || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/devotionals/today", form);
      setDevo(data);
      setEditOpen(false);
      toast.success("Devotional updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  // Loading skeleton (keeps layout height stable)
  if (loading) {
    return (
      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 animate-pulse" data-testid="devotional-loading">
        <div className="h-3 w-28 bg-terracotta/20 rounded mb-4" />
        <div className="h-4 w-48 bg-brand-primary/10 rounded mb-6" />
        <div className="space-y-2">
          <div className="h-3 w-full bg-brand-primary/10 rounded" />
          <div className="h-3 w-5/6 bg-brand-primary/10 rounded" />
          <div className="h-3 w-2/3 bg-brand-primary/10 rounded" />
        </div>
      </Card>
    );
  }

  if (!devo) {
    return (
      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 flex items-start gap-4" data-testid="devotional-error">
        <BookOpen className="h-5 w-5 text-terracotta mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-brand-primary">Today's devotional isn't available right now.</p>
          {isAdmin && (
            <Button onClick={regenerate} disabled={regenerating} className="mt-3 rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-9 px-4">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${regenerating ? "animate-spin" : ""}`} /> Try again
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        data-testid="devotional-card"
        className="rounded-2xl shadow-warm-lg overflow-hidden relative border-0"
        style={{
          background:
            "linear-gradient(135deg, #FFF9F2 0%, #FFF9F2 55%, rgba(198,158,95,0.18) 100%)",
        }}
      >
        <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-terracotta/10 blur-3xl -translate-y-10 translate-x-10 pointer-events-none" />
        <div className="p-7 md:p-8 relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-terracotta/15 grid place-items-center">
                <BookOpen className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">
                  Today's Devotional
                </p>
                <p className="font-heading text-xl md:text-2xl font-semibold text-brand-primary mt-0.5" data-testid="devotional-reference">
                  {devo.reference}
                  <span className="text-brand-primary/55 font-normal text-base ml-2">· {devo.translation || "ESV"}</span>
                  {devo.scripture_source === "ai_fallback" && (
                    <span
                      data-testid="devotional-fallback-badge"
                      title={devo.scripture_error || "ESV API not configured; scripture text rendered by AI."}
                      className="ml-2 align-middle inline-flex items-center gap-1 rounded-full bg-peach/30 text-terracotta text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5"
                    >
                      AI fallback
                    </span>
                  )}
                </p>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openEdit}
                  data-testid="devotional-edit-button"
                  className="text-brand-primary/70 hover:text-terracotta"
                >
                  <Edit3 className="h-4 w-4 mr-1.5" /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={regenerate}
                  disabled={regenerating}
                  data-testid="devotional-regenerate-button"
                  className="text-brand-primary/70 hover:text-terracotta"
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${regenerating ? "animate-spin" : ""}`} />
                  Regenerate
                </Button>
              </div>
            )}
          </div>

          <div
            className="mt-5 pl-5 border-l-2 border-terracotta/40 text-brand-primary/85 text-[15px] leading-relaxed font-serif italic"
            data-testid="devotional-scripture"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
          >
            {formatEsvText(devo.esv_text)}
          </div>

          <div className="mt-6 pt-5 border-t border-brand-primary/10 flex items-start gap-3">
            <Sparkles className="h-4 w-4 text-terracotta mt-1 shrink-0" strokeWidth={1.8} />
            <p className="text-brand-primary/90 leading-relaxed" data-testid="devotional-body">
              {devo.devotional}
            </p>
          </div>

          {devo.source === "manual" && (
            <p className="text-[11px] text-brand-primary/45 mt-4 uppercase tracking-wider">
              Edited by staff · {devo.reference}
            </p>
          )}
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit today's devotional</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-brand-primary/60">Reference</label>
              <Input
                data-testid="devotional-edit-reference"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. Philippians 1:3-11"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-brand-primary/60">
                Scripture text (ESV)
              </label>
              <Textarea
                data-testid="devotional-edit-scripture"
                rows={7}
                value={form.esv_text}
                onChange={(e) => setForm({ ...form, esv_text: e.target.value })}
                placeholder="[1] The passage text with verse markers…"
                className="font-serif"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-brand-primary/60">Devotional paragraph</label>
              <Textarea
                data-testid="devotional-edit-body"
                rows={6}
                value={form.devotional}
                onChange={(e) => setForm({ ...form, devotional: e.target.value })}
                placeholder="Your reflection (3-5 sentences)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="devotional-edit-save"
              onClick={saveEdit}
              disabled={saving}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
